import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import "animate.css";
import { DndContext, closestCenter, MouseSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import config from "../config";
import "../assets/css/AllTablesQueueManagement.css";

const capitalizeFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const tableTypeLabels = {
    snooker: "Snooker",
    pool: "Pool",
    "us-pool": "US pool",
    "chinese-pool": "Heyball",
};

const formatTableType = type => tableTypeLabels[type] || capitalizeFirst(type);

const QueueIcon = ({ name }) => {
    const paths = {
        arrow: <><path d="M19 12H5" /><path d="m11 18-6-6 6-6" /></>,
        external: <><path d="M15 3h6v6" /><path d="m10 14 11-11" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
        people: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
        tables: <><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M7 9h10M7 15h10M9 5v14M15 5v14" /></>,
        active: <><path d="M20 7h-9" /><path d="M14 17H5" /><circle cx="17" cy="17" r="3" /><circle cx="8" cy="7" r="3" /></>,
        plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
        queue: <><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><circle cx="3" cy="6" r="1" /><circle cx="3" cy="12" r="1" /><circle cx="3" cy="18" r="1" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" /></>,
        trash: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 15H6L5 6" /><path d="M10 11v5M14 11v5" /></>,
        check: <path d="m5 12 4 4L19 6" />,
        close: <><path d="m6 6 12 12" /><path d="m18 6-12 12" /></>,
        grip: <><circle cx="9" cy="5" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="5" r="1" fill="currentColor" stroke="none" /><circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="9" cy="19" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="19" r="1" fill="currentColor" stroke="none" /></>,
        retry: <><path d="M20 11a8.1 8.1 0 1 0 .3 4" /><path d="M20 4v7h-7" /></>,
        sparkle: <><path d="m12 3 1.25 3.75L17 8l-3.75 1.25L12 13l-1.25-3.75L7 8l3.75-1.25Z" /><path d="m19 15 .75 2.25L22 18l-2.25.75L19 21l-.75-2.25L16 18l2.25-.75Z" /></>,
    };

    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {paths[name]}
        </svg>
    );
};

const queuePageCache = new Map();

const AllTablesQueueManagement = () => {
    const navigate = useNavigate();
    const { club_id } = useParams();
    const { user, loading: authLoading } = useAuth();
    const cachedQueueData = queuePageCache.get(String(club_id));
    const [tables, setTables] = useState(() => cachedQueueData?.tables || []);
    const [queues, setQueues] = useState(() => cachedQueueData?.queues || {});
    const [playerNames, setPlayerNames] = useState({});
    const [loading, setLoading] = useState(() => !cachedQueueData);
    const [isSaving, setIsSaving] = useState({});
    const [isAdding, setIsAdding] = useState({});
    const [animatedItems, setAnimatedItems] = useState({});
    const [removedItems, setRemovedItems] = useState([]);
    const prevQueuesRef = useRef({});
    const fetchInFlightRef = useRef(null);
    const hasLoadedRef = useRef(Boolean(cachedQueueData));
    const [activeTab, setActiveTab] = useState("snooker");
    const [loadError, setLoadError] = useState("");

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { pressDelay: 150, activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const fetchTablesAndQueues = useCallback(async ({ background = false } = {}) => {
        if (!user?.id) return null;
        if (fetchInFlightRef.current) return fetchInFlightRef.current;

        const request = (async () => {
            if (!background && !hasLoadedRef.current) setLoading(true);
            try {
                const [tableData, queueData] = await Promise.all([
                    apiService.post("/club/tables", { id: user.id }),
                    apiService.get(`/public/tables/queues/${user.id}`),
                ]);

                if (tableData?.success === false || queueData?.success === false) {
                    throw new Error(tableData?.message || queueData?.message || "Queue data was unavailable");
                }

                const nextTables = Array.isArray(tableData?.tables) ? tableData.tables : [];
                const queuesMap = nextTables.reduce((acc, table) => {
                    acc[table.id] = [];
                    return acc;
                }, {});

                (Array.isArray(queueData?.tables) ? queueData.tables : []).forEach(table => {
                    queuesMap[table.table_id] = [...(table.queues || [])].sort((a, b) => a.position - b.position);
                });

                setTables(nextTables);
                setQueues(queuesMap);
                queuePageCache.set(String(club_id), { tables: nextTables, queues: queuesMap });
                setPlayerNames(previous => nextTables.reduce((acc, table) => ({ ...acc, [table.id]: previous[table.id] || "" }), {}));
                setIsSaving(previous => nextTables.reduce((acc, table) => ({ ...acc, [table.id]: previous[table.id] || false }), {}));
                setIsAdding(previous => nextTables.reduce((acc, table) => ({ ...acc, [table.id]: previous[table.id] || false }), {}));
                setLoadError("");
                hasLoadedRef.current = true;
                return true;
            } catch (error) {
                console.error("Failed to fetch tables or queues:", error);
                if (!hasLoadedRef.current) {
                    setLoadError("We couldn't load your tables and queues. Please try again.");
                }
                return false;
            } finally {
                setLoading(false);
            }
        })();

        fetchInFlightRef.current = request;
        request.finally(() => {
            if (fetchInFlightRef.current === request) fetchInFlightRef.current = null;
        });
        return request;
    }, [club_id, user?.id]);

    const detectQueueChanges = useCallback((newQueues, tableId) => {
        const prevQueues = prevQueuesRef.current[tableId] || [];
        const newAnimatedItems = {};
        const newRemovedItems = [];

        const prevItems = prevQueues.map(queue => ({
            id: queue.id,
            table_id: tableId,
            player_name: queue.player_name,
            position: queue.position
        }));
        const newItems = newQueues.map(queue => ({
            id: queue.id,
            table_id: tableId,
            player_name: queue.player_name,
            position: queue.position
        }));

        newItems.forEach(newItem => {
            const prevItem = prevItems.find(item => item.id === newItem.id);
            if (!prevItem) {
                newAnimatedItems[`${tableId}-${newItem.id}`] = "animate__slideInUp";
            } else if (prevItem.player_name !== newItem.player_name) {
                newAnimatedItems[`${tableId}-${newItem.id}`] = "animate__pulse";
            } else if (prevItem.position !== newItem.position) {
                newAnimatedItems[`${tableId}-${newItem.id}`] = "animate__bounce";
            }
        });

        prevItems.forEach(prevItem => {
            if (!newItems.find(item => item.id === prevItem.id)) {
                newRemovedItems.push({ ...prevItem, animation: "animate__fadeOut" });
            }
        });

        setAnimatedItems(previous => ({ ...previous, ...newAnimatedItems }));
        if (newRemovedItems.length > 0) setRemovedItems(previous => [...previous, ...newRemovedItems]);

        setTimeout(() => {
            setAnimatedItems(prev => {
                const updated = { ...prev };
                newItems.forEach(item => delete updated[`${tableId}-${item.id}`]);
                return updated;
            });
            setRemovedItems(prev => prev.filter(item => item.table_id !== tableId));
        }, 1000);

        prevQueuesRef.current[tableId] = JSON.parse(JSON.stringify(newQueues));
    }, []);

    const addToQueue = async (tableId) => {
        const name = capitalizeFirst(playerNames[tableId]?.trim());
        if (!name) {
            alert("Please enter a player name");
            return;
        }
        setIsAdding(prev => ({ ...prev, [tableId]: true }));
        try {
            const response = await apiService.post("/table/queue/add", {
                table_id: tableId,
                player_name: name,
                id_user: user.id,
            });
            if (response.success) {
                setPlayerNames(prev => ({ ...prev, [tableId]: "" }));
                const queueData = await apiService.post("/table/queue", { table_id: tableId, id_user: user.id });
                const newQueues = (queueData.queues || []).sort((a, b) => a.position - b.position);
                setQueues(prev => ({ ...prev, [tableId]: newQueues }));
                detectQueueChanges(newQueues, tableId);
            } else {
                alert(response.message || "Failed to add player to queue");
            }
        } catch (e) {
            console.error("Failed to add to queue:", e);
            alert("Failed to add player to queue");
        } finally {
            setIsAdding(prev => ({ ...prev, [tableId]: false }));
        }
    };

    const removeFromQueue = async (tableId, queueId) => {
        try {
            const response = await apiService.post("/table/queue/remove", {
                queue_id: queueId,
                id_user: user.id,
            });
            if (response.success) {
                const queueData = await apiService.post("/table/queue", { table_id: tableId, id_user: user.id });
                const newQueues = (queueData.queues || []).sort((a, b) => a.position - b.position);
                setQueues(prev => ({ ...prev, [tableId]: newQueues }));
                detectQueueChanges(newQueues, tableId);
                return true;
            } else {
                alert(response.message || "Failed to remove player from queue");
                return false;
            }
        } catch (e) {
            console.error("Failed to remove from queue:", e);
            alert("Failed to remove player from queue");
            return false;
        }
    };

    const renumber = (arr) => arr.map((item, idx) => ({ ...item, position: idx + 1 }));

    const persistOrder = async (tableId, newQueue) => {
        setIsSaving(prev => ({ ...prev, [tableId]: true }));
        const previousQueue = prevQueuesRef.current[tableId] || [];
        try {
            prevQueuesRef.current[tableId] = newQueue;

            const order = newQueue.map((q, i) => ({ id: q.id, position: i + 1 }));
            const res = await apiService.post("/table/queue/reorder", {
                table_id: tableId,
                id_user: user.id,
                order,
            });

            if (!res.success) {
                setQueues(current => ({ ...current, [tableId]: previousQueue }));
                alert(res.message || "Failed to save new order");
            } else if (Array.isArray(res.queues)) {
                const newQueues = res.queues.sort((a, b) => a.position - b.position);
                setQueues(prev => ({ ...prev, [tableId]: newQueues }));
                detectQueueChanges(newQueues, tableId);
            }
        } catch (e) {
            console.error("Failed to persist order:", e);
            alert("Failed to save new order");
            setQueues(current => ({ ...current, [tableId]: previousQueue }));
        } finally {
            setIsSaving(prev => ({ ...prev, [tableId]: false }));
        }
    };

    const saveName = async (tableId, queueId, newName) => {
        const name = capitalizeFirst(newName.trim());
        if (!name) {
            alert("Name cannot be empty");
            return false;
        }
        try {
            const res = await apiService.post("/table/queue/edit", {
                queue_id: queueId,
                player_name: name,
                id_user: user.id,
            });
            if (!res.success) {
                alert(res.message || "Failed to update name");
                return false;
            }
            if (Array.isArray(res.queues)) {
                const newQueues = res.queues.sort((a, b) => a.position - b.position);
                setQueues(prev => ({ ...prev, [tableId]: newQueues }));
                detectQueueChanges(newQueues, tableId);
            } else {
                setQueues(prev => ({
                    ...prev,
                    [tableId]: prev[tableId].map(q => (q.id === queueId ? { ...q, player_name: name } : q))
                }));
                detectQueueChanges(queues[tableId], tableId);
            }
            return true;
        } catch (e) {
            console.error("Failed to update name:", e);
            alert("Failed to update name");
            return false;
        }
    };

    const handleDragEnd = (tableId) => async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const queue = queues[tableId] || [];
        const oldIndex = queue.findIndex(q => q.id === active.id);
        const newIndex = queue.findIndex(q => q.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const moved = arrayMove(queue, oldIndex, newIndex);
        const withPositions = renumber(moved);
        setQueues(prev => ({ ...prev, [tableId]: withPositions }));
        await persistOrder(tableId, withPositions);
    };

    useEffect(() => {
        if (authLoading || !user?.id) return undefined;

        fetchTablesAndQueues({ background: hasLoadedRef.current });

        const socket = io(config.SOCKET_URL, { transports: ['polling', 'websocket'] });
        socket.on("connect", () => {
            console.log("Socket connected:", socket.id);
            socket.emit("joinClubQueue", club_id);
        });

        socket.on("initialQueueState", ({ clubId, tables }) => {
            console.log("Initial Queue State:", JSON.stringify({ clubId, tables, timestamp: new Date().toISOString() }));
            if (String(clubId) === String(club_id)) {
                if (Array.isArray(tables) && tables.length > 0) {
                    const queuesMap = tables.reduce((acc, table) => {
                        acc[table.table_id] = (table.queues || []).sort((a, b) => a.position - b.position);
                        return acc;
                    }, {});
                    setQueues(prev => ({ ...prev, ...queuesMap }));
                    Object.keys(queuesMap).forEach(tableId => detectQueueChanges(queuesMap[tableId], tableId));
                    console.log("Updated queues from initialQueueState:", JSON.stringify(queuesMap));
                } else {
                    console.warn("Empty or invalid tables from initialQueueState; keeping API data");
                }
            } else {
                console.warn(`Club ID mismatch: received ${clubId}, expected ${club_id}`);
            }
        });

        socket.on("queueUpdate", ({ clubId, tables }) => {
            console.log("Queue Update Received:", JSON.stringify({ clubId, tables, timestamp: new Date().toISOString() }));
            if (String(clubId) === String(club_id)) {
                if (Array.isArray(tables) && tables.length > 0) {
                    const queuesMap = tables.reduce((acc, table) => {
                        acc[table.table_id] = (table.queues || []).sort((a, b) => a.position - b.position);
                        return acc;
                    }, {});
                    setQueues(prev => ({ ...prev, ...queuesMap }));
                    Object.keys(queuesMap).forEach(tableId => detectQueueChanges(queuesMap[tableId], tableId));
                    console.log("Updated queues from queueUpdate:", JSON.stringify(queuesMap));
                } else {
                    console.warn("Empty or invalid tables from queueUpdate; keeping current data");
                }
            } else {
                console.warn(`Club ID mismatch in queueUpdate: received ${clubId}, expected ${club_id}`);
            }
        });

        socket.on("connect_error", (error) => {
            console.warn("Socket connection error:", error.message);
        });

        const pollInterval = setInterval(() => fetchTablesAndQueues({ background: true }), 30000);

        return () => {
            socket.disconnect();
            clearInterval(pollInterval);
        };
    }, [authLoading, user?.id, club_id, detectQueueChanges, fetchTablesAndQueues]);

    if (loading) {
        return (
            <main className="page-wrapper club-queue-page club-queue-page-loading" aria-busy="true">
                <div className="club-queue-container">
                    <div className="club-queue-loading-copy">
                        <span className="club-queue-kicker"><i /> Live floor operations</span>
                        <div className="club-queue-skeleton club-queue-skeleton-title" />
                        <div className="club-queue-skeleton club-queue-skeleton-copy" />
                    </div>
                    <div className="club-queue-loading-grid" role="status" aria-label="Loading table queues">
                        {[0, 1, 2].map(item => (
                            <div className="club-queue-loading-card" key={item}>
                                <div className="club-queue-skeleton club-queue-skeleton-card-title" />
                                <div className="club-queue-skeleton club-queue-skeleton-input" />
                                <div className="club-queue-skeleton club-queue-skeleton-row" />
                                <div className="club-queue-skeleton club-queue-skeleton-row club-queue-skeleton-row-short" />
                            </div>
                        ))}
                        <span className="visually-hidden">Loading queues…</span>
                    </div>
                </div>
            </main>
        );
    }

    if (loadError && tables.length === 0) {
        return (
            <main className="page-wrapper club-queue-page">
                <div className="club-queue-container club-queue-error-wrap">
                    <section className="club-queue-error" role="alert">
                        <span className="club-queue-error-icon"><QueueIcon name="retry" /></span>
                        <span className="club-queue-kicker">Connection interrupted</span>
                        <h1>Queues are temporarily unavailable</h1>
                        <p>{loadError}</p>
                        <div className="club-queue-error-actions">
                            <button className="club-queue-button club-queue-button-primary" type="button" onClick={() => fetchTablesAndQueues()}>
                                <QueueIcon name="retry" />
                                Try again
                            </button>
                            <button className="club-queue-button club-queue-button-secondary" type="button" onClick={() => navigate("/")}>
                                Back to overview
                            </button>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    const tableTypes = ["snooker", "pool", "us-pool", "chinese-pool"];
    const groupedTables = tableTypes.reduce((acc, type) => {
        acc[type] = tables.filter(table => table.type === type);
        return acc;
    }, {});
    const currentTables = groupedTables[activeTab] || [];
    const totalQueuedPlayers = Object.values(queues).reduce((total, queue) => total + (queue?.length || 0), 0);
    const activeQueues = tables.filter(table => (queues[table.id]?.length || 0) > 0).length;

    return (
        <main className="page-wrapper club-queue-page">
            <div className="club-queue-glow club-queue-glow-one" aria-hidden="true" />
            <div className="club-queue-glow club-queue-glow-two" aria-hidden="true" />

            <div className="club-queue-container">
                <div className="club-queue-topline">
                    <button className="club-queue-back" type="button" onClick={() => navigate("/")}>
                        <QueueIcon name="arrow" />
                        Back to overview
                    </button>
                    <a
                        href={`/public/queue/${club_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="club-queue-public-link"
                    >
                        Open public queue
                        <QueueIcon name="external" />
                    </a>
                </div>

                <header className="club-queue-hero">
                    <div className="club-queue-hero-copy">
                        <span className="club-queue-kicker"><i /> Live floor operations</span>
                        <h1>All table queues</h1>
                        <p>Keep every waiting list moving from one calm, real-time workspace.</p>
                    </div>
                    <dl className="club-queue-summary" aria-label="Queue summary">
                        <div>
                            <dt><span className="club-queue-summary-icon club-queue-summary-icon-lime"><QueueIcon name="tables" /></span>Tables</dt>
                            <dd>{tables.length}</dd>
                        </div>
                        <div>
                            <dt><span className="club-queue-summary-icon club-queue-summary-icon-mint"><QueueIcon name="people" /></span>Waiting</dt>
                            <dd>{totalQueuedPlayers}</dd>
                        </div>
                        <div>
                            <dt><span className="club-queue-summary-icon club-queue-summary-icon-sand"><QueueIcon name="active" /></span>Active queues</dt>
                            <dd>{activeQueues}</dd>
                        </div>
                    </dl>
                </header>

                <section className="club-queue-workspace" aria-labelledby="queue-workspace-heading">
                    <div className="club-queue-workspace-heading">
                        <div>
                            <span className="club-queue-section-label">Queue board</span>
                            <h2 id="queue-workspace-heading">Choose a table type</h2>
                        </div>
                        <span className="club-queue-live-note"><i /> Updates live</span>
                    </div>

                    <div className="club-queue-tabs" role="tablist" aria-label="Table types">
                        {tableTypes.map(type => {
                            const typeQueueCount = groupedTables[type].reduce((total, table) => total + (queues[table.id]?.length || 0), 0);
                            return (
                                <button
                                    className={`club-queue-tab ${activeTab === type ? "is-active" : ""}`}
                                    id={`queue-tab-${type}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === type}
                                    aria-controls="queue-panel"
                                    onClick={() => setActiveTab(type)}
                                    key={type}
                                >
                                    <span>{formatTableType(type)}</span>
                                    <small>{groupedTables[type].length} tables</small>
                                    {typeQueueCount > 0 && <b aria-label={`${typeQueueCount} waiting`}>{typeQueueCount}</b>}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="club-queue-panel"
                        id="queue-panel"
                        role="tabpanel"
                        aria-labelledby={`queue-tab-${activeTab}`}
                        key={activeTab}
                    >
                        {currentTables.length > 0 ? (
                            <div className="club-queue-card-grid">
                                {currentTables.map((table, index) => {
                                    const tableQueue = queues[table.id] || [];
                                    const hasQueue = tableQueue.length > 0 || removedItems.some(item => item.table_id === table.id);
                                    return (
                                        <article
                                            className="club-queue-card animate__animated animate__fadeIn"
                                            style={{ "--queue-card-delay": `${Math.min(index, 5) * 45}ms` }}
                                            key={table.id}
                                        >
                                            <header className="club-queue-card-header">
                                                <div className="club-queue-table-identity">
                                                    <span className="club-queue-table-mark"><QueueIcon name="tables" /></span>
                                                    <div>
                                                        <span>{formatTableType(table.type)}</span>
                                                        <h3>{table.name}</h3>
                                                    </div>
                                                </div>
                                                <span className={`club-queue-count ${tableQueue.length > 0 ? "has-players" : ""}`}>
                                                    <strong>{tableQueue.length}</strong>
                                                    waiting
                                                </span>
                                            </header>

                                            <div className="club-queue-card-body">
                                                <form
                                                    className="club-queue-add-form"
                                                    onSubmit={event => {
                                                        event.preventDefault();
                                                        addToQueue(table.id);
                                                    }}
                                                >
                                                    <label className="visually-hidden" htmlFor={`queue-player-${table.id}`}>Player name for {table.name}</label>
                                                    <input
                                                        id={`queue-player-${table.id}`}
                                                        type="text"
                                                        placeholder="Enter player name"
                                                        value={playerNames[table.id] || ""}
                                                        onChange={event => setPlayerNames(prev => ({ ...prev, [table.id]: capitalizeFirst(event.target.value) }))}
                                                        autoCapitalize="words"
                                                        autoComplete="off"
                                                    />
                                                    <button
                                                        type="submit"
                                                        disabled={isAdding[table.id] || isSaving[table.id]}
                                                    >
                                                        {isAdding[table.id] ? (
                                                            <><span className="club-queue-button-spinner" aria-hidden="true" />Adding</>
                                                        ) : (
                                                            <><QueueIcon name="plus" />Add player</>
                                                        )}
                                                    </button>
                                                </form>

                                                <div className="club-queue-list-heading">
                                                    <span><QueueIcon name="queue" />Waiting list</span>
                                                    {isSaving[table.id] && <small role="status">Saving order…</small>}
                                                </div>

                                                {hasQueue ? (
                                                    <DndContext
                                                        sensors={sensors}
                                                        collisionDetection={closestCenter}
                                                        onDragEnd={handleDragEnd(table.id)}
                                                    >
                                                        <SortableContext
                                                            items={tableQueue.map(queue => queue.id)}
                                                            strategy={verticalListSortingStrategy}
                                                        >
                                                            <ol className="club-queue-waiting-list">
                                                                {tableQueue.map(item => (
                                                                    <SortableRow
                                                                        key={item.id}
                                                                        item={item}
                                                                        tableId={table.id}
                                                                        onRemove={() => removeFromQueue(table.id, item.id)}
                                                                        onSaveName={saveName}
                                                                        isSavingGlobal={isSaving[table.id]}
                                                                        animatedItems={animatedItems}
                                                                    />
                                                                ))}
                                                                {removedItems
                                                                    .filter(item => item.table_id === table.id)
                                                                    .map(item => (
                                                                        <li
                                                                            key={item.id}
                                                                            className={`club-queue-player club-queue-player-removed animate__animated ${item.animation}`}
                                                                        >
                                                                            <span className="club-queue-position">{item.position}</span>
                                                                            <span>{item.player_name}</span>
                                                                        </li>
                                                                    ))}
                                                            </ol>
                                                        </SortableContext>
                                                    </DndContext>
                                                ) : (
                                                    <div className="club-queue-empty-list">
                                                        <span><QueueIcon name="sparkle" /></span>
                                                        <div>
                                                            <strong>Ready for the next player</strong>
                                                            <p>This table has no one waiting.</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="club-queue-empty-type animate__animated animate__fadeIn">
                                <span className="club-queue-empty-type-icon"><QueueIcon name="tables" /></span>
                                <span className="club-queue-section-label">Nothing to manage</span>
                                <h3>No {formatTableType(activeTab)} tables yet</h3>
                                <p>When you add this table type to your club, its queue will appear here automatically.</p>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
};

function SortableRow({ item, tableId, onRemove, onSaveName, isSavingGlobal, animatedItems }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isSavingName, setIsSavingName] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [name, setName] = useState(item.player_name);

    useEffect(() => setName(item.player_name), [item.player_name]);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const startEdit = () => setIsEditing(true);
    const cancelEdit = () => {
        setName(item.player_name);
        setIsEditing(false);
    };
    const doSave = async () => {
        setIsSavingName(true);
        const ok = await onSaveName(tableId, item.id, name);
        setIsSavingName(false);
        if (ok) setIsEditing(false);
    };
    const onKeyDown = (e) => {
        if (e.key === "Enter") doSave();
        if (e.key === "Escape") cancelEdit();
    };

    const doDelete = async () => {
        setIsDeleting(true);
        await onRemove();
        setIsDeleting(false);
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            className={`club-queue-player animate__animated ${animatedItems[`${tableId}-${item.id}`] || ""
            } ${isDragging ? "dragging" : ""}`}
        >
            <div className="club-queue-player-main">
                {!isEditing ? (
                    <button
                        className="club-queue-drag-handle"
                        type="button"
                        title="Drag to reorder"
                        aria-label={`Drag to reorder ${item.player_name}`}
                        {...attributes}
                        {...listeners}
                    >
                        <QueueIcon name="grip" />
                    </button>
                ) : (
                    <span className="club-queue-drag-placeholder" aria-hidden="true" />
                )}
                <span className="club-queue-position" aria-label={`Position ${item.position}`}>{item.position}</span>
                <div className="club-queue-player-details">
                    {!isEditing ? (
                        <span className="club-queue-player-name">{item.player_name}</span>
                    ) : (
                        <input
                            className="club-queue-edit-input"
                            value={name}
                            onChange={e => setName(capitalizeFirst(e.target.value))}
                            onKeyDown={onKeyDown}
                            autoFocus
                            inputMode="text"
                            placeholder="Player name"
                            aria-label={`Edit ${item.player_name}`}
                            disabled={isSavingName}
                        />
                    )}
                </div>
            </div>
            <div className="club-queue-row-actions">
                {!isEditing ? (
                    <>
                        <button
                            className="club-queue-row-action"
                            type="button"
                            onClick={startEdit}
                            disabled={isSavingGlobal}
                            aria-label={`Edit ${item.player_name}`}
                        >
                            <QueueIcon name="edit" />
                            <span>Edit</span>
                        </button>
                        <button
                            className="club-queue-row-action is-danger"
                            type="button"
                            onClick={doDelete}
                            disabled={isSavingGlobal || isDeleting}
                            aria-label={`Remove ${item.player_name} from the queue`}
                        >
                            {isDeleting ? (
                                <><span className="club-queue-button-spinner" aria-hidden="true" /><span>Removing</span></>
                            ) : (
                                <><QueueIcon name="trash" /><span>Remove</span></>
                            )}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className="club-queue-row-action is-primary"
                            type="button"
                            onClick={doSave}
                            disabled={isSavingName}
                            aria-label={`Save ${item.player_name}`}
                        >
                            {isSavingName ? (
                                <><span className="club-queue-button-spinner" aria-hidden="true" /><span>Saving</span></>
                            ) : (
                                <><QueueIcon name="check" /><span>Save</span></>
                            )}
                        </button>
                        <button
                            className="club-queue-row-action"
                            type="button"
                            onClick={cancelEdit}
                            disabled={isSavingName}
                            aria-label="Cancel editing"
                        >
                            <QueueIcon name="close" />
                            <span>Cancel</span>
                        </button>
                    </>
                )}
            </div>
        </li>
    );
}

export default AllTablesQueueManagement;
